/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type {
  DeploymentResourceCommandResponse,
  TransientElasticsearchPlanConfiguration,
} from '@modules/cloud-api/v1/types'

import { VACATE_ES_CLUSTER, VACATE_ES_CLUSTER_VALIDATE } from '../../constants/actions'
import { replaceIn } from '../../lib/immutability-helpers'

import type { AsyncAction, ReduxState } from '@/types/redux'

export interface State {
  vacates: {
    [crumbs: string]: DeploymentResourceCommandResponse | undefined
  }
  validateCalls: {
    [crumbs: string]: TransientElasticsearchPlanConfiguration | undefined
  }
}

interface VacateEsClusterAction
  extends AsyncAction<typeof VACATE_ES_CLUSTER, DeploymentResourceCommandResponse> {
  meta: {
    deploymentId: string
    refId: string
    instanceIds: string[]
  }
}

interface VacateEsClusterValidateAction
  extends AsyncAction<typeof VACATE_ES_CLUSTER_VALIDATE, DeploymentResourceCommandResponse> {
  meta: {
    deploymentId: string
    refId: string
    instanceIds: string[]
  }
}

const initialState: State = {
  vacates: {},
  validateCalls: {},
}

export default function vacateEsClustersReducer(
  state: State = initialState,
  action: VacateEsClusterAction | VacateEsClusterValidateAction,
): State {
  if (action.type === VACATE_ES_CLUSTER) {
    const { deploymentId, refId, instanceIds } = action.meta
    const descriptor = createDescriptor(deploymentId, refId, instanceIds)

    if (!action.error && action.payload) {
      return replaceIn(state, ['vacates', descriptor], action.payload)
    }

    return replaceIn(state, ['vacates', descriptor], undefined)
  }

  if (action.type === VACATE_ES_CLUSTER_VALIDATE) {
    const { deploymentId, refId, instanceIds } = action.meta
    const descriptor = createDescriptor(deploymentId, refId, instanceIds)

    if (!action.error && action.payload) {
      return replaceIn(state, ['validateCalls', descriptor], action.payload)
    }

    return replaceIn(state, ['vacates', descriptor], undefined)
  }

  return state
}

function _getEsClusterVacateValidate(
  state: State,
  deploymentId: string,
  refId: string,
  instanceIds: string[],
): TransientElasticsearchPlanConfiguration | undefined {
  return state.validateCalls[createDescriptor(deploymentId, refId, instanceIds)]
}

function createDescriptor(deploymentId: string, refId: string, instanceIds: string[]): string {
  return `${deploymentId}/${refId}/{${instanceIds.join(',')}}`
}

export function getEsClusterVacateValidate(
  state: ReduxState,
  regionId: string,
  clusterId: string,
  instanceIds: string[],
) {
  return _getEsClusterVacateValidate(state.vacateEsClusters, regionId, clusterId, instanceIds)
}
